<template>
  <div class="mx-4 mt-4">
    <!-- Stats Cards Row 1 -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_PRODUCTS')"
          type="gradient-primary"
          :sub-title="formatNumber(stats.totalProducts)"
          icon="fas fa-boxes"
        >
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_SALES')"
          type="gradient-green"
          :sub-title="formatCurrency(stats.totalSalesAmount)"
          icon="fas fa-cash-register"
        >
          <template slot="footer">
            <span class="text-nowrap">{{ formatNumber(stats.totalSalesQuantity) }} {{ $t('COMMON.UNITS') }}</span>
          </template>
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_PURCHASES')"
          type="gradient-orange"
          :sub-title="formatCurrency(stats.totalPurchasesAmount)"
          icon="fas fa-shopping-cart"
        >
          <template slot="footer">
            <span class="text-nowrap">{{ formatNumber(stats.totalPurchasesQuantity) }} {{ $t('COMMON.UNITS') }}</span>
          </template>
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_TRANSFERS_INCOMING')"
          type="gradient-info"
          :sub-title="formatCurrency(stats.totalTransfersIncomingAmount)"
          icon="fas fa-arrow-circle-down"
        >
          <template slot="footer">
            <span class="text-nowrap">{{ formatNumber(stats.totalTransfersIncomingQuantity) }} {{ $t('COMMON.UNITS') }}</span>
          </template>
        </stats-card>
      </div>
    </div>

    <!-- Stats Cards Row 2 -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_TRANSFERS_OUTGOING')"
          type="gradient-info"
          :sub-title="formatCurrency(stats.totalTransfersOutgoingAmount)"
          icon="fas fa-arrow-circle-up"
        >
          <template slot="footer">
            <span class="text-nowrap">{{ formatNumber(stats.totalTransfersOutgoingQuantity) }} {{ $t('COMMON.UNITS') }}</span>
          </template>
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_INTERNAL_INVOICES_INCOMING')"
          type="gradient-purple"
          :sub-title="formatCurrency(stats.totalInternalInvoicesIncomingAmount)"
          icon="fas fa-file-invoice"
        >
          <template slot="footer">
            <span class="text-nowrap">{{ formatNumber(stats.totalInternalInvoicesIncomingQuantity) }} {{ $t('COMMON.UNITS') }}</span>
          </template>
        </stats-card>
      </div>
      <div class="col-xl-3 col-md-6">
        <stats-card
          :title="$t('COMMON.TOTAL_INTERNAL_INVOICES_OUTGOING')"
          type="gradient-purple"
          :sub-title="formatCurrency(stats.totalInternalInvoicesOutgoingAmount)"
          icon="fas fa-file-export"
        >
          <template slot="footer">
            <span class="text-nowrap">{{ formatNumber(stats.totalInternalInvoicesOutgoingQuantity) }} {{ $t('COMMON.UNITS') }}</span>
          </template>
        </stats-card>
      </div>
    </div>

    <div class="card col-12">
      <div class="card-header border-0">
        <div class="row align-items-center">
          <div class="col">
            <h3 class="mb-0">{{ $t("COMMON.PRODUCTS_REPORT") }}</h3>
          </div>
        </div>
      </div>

      <div class="card-body">
        <!-- Filters Section -->
        <div class="row mb-4">
          <div
            class="col-md-3"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
          >
            <base-input :label="$t('COMMON.ORGANIZATION')">
              <organization-selector
                :organization="filters.organization_id"
                :filterable="true"
                :showAll="false"
                @organizationChanged="
                  (organizationId) => {
                    filters.organization_id = organizationId;
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.ESTABLISHMENTS')">
              <establishments-selector
                :establishments="filters.establishments"
                :showAll="false"
                :filterOrganization="filters.organization_id"
                @establishmentsChanged="
                  (establishments) => {
                    filters.establishments = establishments || [];
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.DATE_RANGE')">
              <flat-picker
                v-model="dateRange"
                :config="dateRangeConfig"
                class="form-control"
                :placeholder="$t('COMMON.DATES_PLACEHOLDER')"
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.PRODUCTS')">
              <products-selector
                :products="filters.products"
                :filterOrganization="filters.organization_id"
                :multiple="true"
                :showAll="false"
                :collapseTags="true"
                @productChanged="
                  (products, productsData) => {
                    filters.products = productsData || [];
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.DOCUMENT_TYPES')">
              <el-select
                v-model="filters.document_types"
                multiple
                :placeholder="$t('COMMON.ALL')"
                class="select-primary"
              >
                <el-option
                  v-for="option in documentTypeOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.SUPPLIER')">
              <supplier-selector
                :supplier="filters.supplier_id"
                :filterOrganization="filters.organization_id"
                :showAll="false"
                :filterable="true"
                @supplierChanged="
                  (supplierId) => {
                    filters.supplier_id = supplierId;
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3 d-flex align-items-center">
            <base-checkbox v-model="filters.by_warehouse" class="mb-3">
              <span class="form-control-label">
                {{ $t("COMMON.BY_WAREHOUSE") }}
              </span>
            </base-checkbox>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col">
            <base-button type="primary" @click="applyFilters">
              {{ $t("COMMON.APPLY_FILTERS") }}
            </base-button>
            <base-button type="secondary" @click="resetFilters">
              {{ $t("COMMON.RESET") }}
            </base-button>
            <base-button
              type="success"
              @click="exportToExcel"
              :loading="exportLoading"
              :disabled="exportLoading"
            >
              <i class="fas fa-file-excel mr-1"></i>
              {{ $t("COMMON.EXPORT_EXCEL") }}
            </base-button>
          </div>
        </div>

        <!-- Results Table -->
        <el-table
          class="align-items-center table-flush"
          header-row-class-name="thead-light"
          :data="reportData"
          v-loading="loading"
          show-summary
          :summary-method="getSummaries"
        >
          <div slot="empty" v-if="loading">
            <img src="/img/loading.gif" style="height: 100px; width: 100px" />
          </div>

          <el-table-column
            v-if="appliedByWarehouse"
            :label="$t('COMMON.WAREHOUSE')"
            prop="warehouse_name"
            min-width="180px"
          >
            <template v-slot="{ row }">
              {{ row.warehouse_name || '-' }}
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('COMMON.CODE')"
            prop="product_code"
            min-width="120px"
          />
          <el-table-column
            :label="$t('COMMON.NAME')"
            prop="product_name"
            min-width="200px"
          />
          <el-table-column
            :label="$t('COMMON.SKU')"
            prop="product_sku"
            min-width="120px"
          />

          <!-- Totals Column -->
          <el-table-column :label="$t('COMMON.TOTALS')" align="center">
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="row_total_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(getRowTotalQuantity(row)) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="row_total_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(getRowTotalAmount(row)) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- Sales Columns -->
          <el-table-column
            v-if="isColumnVisible('sales')"
            :label="$t('COMMON.SALES')"
            align="center"
          >
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="total_sales_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(row.total_sales_quantity) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="total_sales_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(row.total_sales_amount) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- Purchases Columns -->
          <el-table-column
            v-if="isColumnVisible('purchases')"
            :label="$t('COMMON.PURCHASES')"
            align="center"
          >
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="total_purchases_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(row.total_purchases_quantity) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="total_purchases_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(row.total_purchases_amount) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- Transfers Incoming Columns -->
          <el-table-column
            v-if="isColumnVisible('transfers_incoming')"
            :label="$t('COMMON.TRANSFERS_INCOMING')"
            align="center"
          >
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="total_transfers_incoming_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(row.total_transfers_incoming_quantity) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="total_transfers_incoming_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(row.total_transfers_incoming_amount) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- Transfers Outgoing Columns -->
          <el-table-column
            v-if="isColumnVisible('transfers_outgoing')"
            :label="$t('COMMON.TRANSFERS_OUTGOING')"
            align="center"
          >
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="total_transfers_outgoing_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(row.total_transfers_outgoing_quantity) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="total_transfers_outgoing_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(row.total_transfers_outgoing_amount) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- Internal Invoices Incoming Columns -->
          <el-table-column
            v-if="isColumnVisible('internal_invoices_incoming')"
            :label="$t('COMMON.INTERNAL_INVOICES_INCOMING')"
            align="center"
          >
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="total_internal_invoices_incoming_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(row.total_internal_invoices_incoming_quantity) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="total_internal_invoices_incoming_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(row.total_internal_invoices_incoming_amount) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- Internal Invoices Outgoing Columns -->
          <el-table-column
            v-if="isColumnVisible('internal_invoices_outgoing')"
            :label="$t('COMMON.INTERNAL_INVOICES_OUTGOING')"
            align="center"
          >
            <el-table-column
              :label="$t('COMMON.QUANTITY')"
              prop="total_internal_invoices_outgoing_quantity"
              min-width="100px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatNumber(row.total_internal_invoices_outgoing_quantity) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.AMOUNT')"
              prop="total_internal_invoices_outgoing_amount"
              min-width="120px"
              align="right"
            >
              <template v-slot="{ row }">
                {{ formatCurrency(row.total_internal_invoices_outgoing_amount) }}
              </template>
            </el-table-column>
          </el-table-column>
        </el-table>

        <!-- Pagination -->
        <div
          class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
        >
          <div class="">
            <p class="card-category">
              {{
                $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
                  from: total ? from + 1 : 0,
                  to: to,
                  of: total,
                })
              }}
            </p>
          </div>
          <base-pagination
            class="pagination-no-border"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import * as XLSX from "xlsx";
import {
  Table,
  TableColumn,
  Select,
  Option,
} from "element-ui";
import { BasePagination, StatsCard } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import ProductsSelector from "@/components/ProductsSelector.vue";
import EstablishmentsSelector from "@/components/EstablishmentsSelector.vue";
import SupplierSelector from "@/components/SupplierSelector.vue";

export default {
  name: "ProductsReportPage",

  components: {
    flatPicker,
    BasePagination,
    StatsCard,
    OrganizationSelector,
    ProductsSelector,
    EstablishmentsSelector,
    SupplierSelector,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  data() {
    const now = new Date();
    const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    const lastDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;

    return {
      dateRange: [firstDay, lastDay],
      exportLoading: false,
      appliedByWarehouse: false,
      filters: {
        organization_id: null,
        establishments: [],
        document_types: [],
        products: [],
        supplier_id: null,
        by_warehouse: false,
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
      },
      dateRangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: true,
        defaultDate: [firstDay, lastDay],
        locale: this.$flatPickrLocale ? this.$flatPickrLocale() : undefined,
      },
      allDocumentTypes: [
        "sales_standard",
        "sales_subsidized",
        "sales_caterer",
        "purchases",
        "transfers_incoming",
        "transfers_outgoing",
        "internal_invoices_incoming",
        "internal_invoices_outgoing",
      ],
    };
  },

  created() {
    this.documentTypeOptions = [
      { value: "sales_standard", label: this.$t("COMMON.SALES_ORDERS_STANDARD") },
      { value: "sales_subsidized", label: this.$t("COMMON.SALES_ORDERS_SUBSIDIZED") },
      { value: "sales_caterer", label: this.$t("COMMON.SALES_ORDERS_CATERER") },
      { value: "purchases", label: this.$t("COMMON.PURCHASES_ORDERS") },
      { value: "transfers_incoming", label: this.$t("COMMON.ESTABLISHMENT_TRANSFERS_INCOMING") },
      { value: "transfers_outgoing", label: this.$t("COMMON.ESTABLISHMENT_TRANSFERS_OUTGOING") },
      { value: "internal_invoices_incoming", label: this.$t("COMMON.ESTABLISHMENT_INTERNAL_INVOICES_INCOMING") },
      { value: "internal_invoices_outgoing", label: this.$t("COMMON.ESTABLISHMENT_INTERNAL_INVOICES_OUTGOING") },
    ];
  },

  computed: {
    ...mapGetters({
      reportData: "productsReport/reportData",
      meta: "productsReport/meta",
      loading: "productsReport/loading",
    }),

    stats() {
      return this.meta?.stats || {
        totalProducts: 0,
        totalSalesQuantity: 0,
        totalSalesAmount: 0,
        totalPurchasesQuantity: 0,
        totalPurchasesAmount: 0,
        totalTransfersIncomingQuantity: 0,
        totalTransfersIncomingAmount: 0,
        totalTransfersOutgoingQuantity: 0,
        totalTransfersOutgoingAmount: 0,
        totalInternalInvoicesIncomingQuantity: 0,
        totalInternalInvoicesIncomingAmount: 0,
        totalInternalInvoicesOutgoingQuantity: 0,
        totalInternalInvoicesOutgoingAmount: 0,
      };
    },

    total() {
      return this.meta?.page?.total || 0;
    },

    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    "pagination.currentPage": {
      handler: "fetchData",
      immediate: false,
    },
  },

  methods: {
    ...mapActions({
      fetchReport: "productsReport/fetchReport",
    }),

    async fetchData() {
      let startDate = undefined;
      let endDate = undefined;

      if (this.dateRange) {
        if (Array.isArray(this.dateRange)) {
          startDate = this.dateRange[0];
          endDate = this.dateRange[1] || this.dateRange[0];
        } else {
          // Handle both English " to " and French " au " separators
          let dates = this.dateRange.split(" to ");
          if (dates.length === 1) {
            dates = this.dateRange.split(" au ");
          }
          if (dates.length === 2) {
            startDate = dates[0].trim();
            endDate = dates[1].trim();
          } else if (dates.length === 1 && dates[0]) {
            startDate = dates[0].trim();
            endDate = dates[0].trim();
          }
        }
      }

      // If no document types selected, use all
      const documentTypes = this.filters.document_types.length > 0
        ? this.filters.document_types
        : this.allDocumentTypes;

      // Extract product IDs from selected products
      const productIds = this.filters.products.length > 0
        ? this.filters.products.map(p => p.id)
        : undefined;

      // Extract establishment IDs from selected establishments
      const establishmentIds = this.filters.establishments.length > 0
        ? this.filters.establishments.map(e => e.id)
        : undefined;

      const params = {
        organization_id: this.filters.organization_id || undefined,
        establishment_ids: establishmentIds,
        start_date: startDate,
        end_date: endDate,
        document_types: documentTypes,
        product_ids: productIds,
        supplier_id: this.filters.supplier_id || undefined,
        by_warehouse: this.filters.by_warehouse ? 1 : undefined,
        page: this.pagination.currentPage,
        per_page: this.pagination.perPage,
      };

      await this.fetchReport(params);
      this.appliedByWarehouse = this.filters.by_warehouse;
    },

    applyFilters() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    resetFilters() {
      const now = new Date();
      const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      const lastDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
      this.dateRange = [firstDay, lastDay];
      this.filters = {
        organization_id: null,
        establishments: [],
        document_types: [],
        products: [],
        supplier_id: null,
        by_warehouse: false,
      };
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    isColumnVisible(type) {
      // If no document types selected, show all columns
      if (this.filters.document_types.length === 0) {
        return true;
      }
      // Quotes are split into pricing-type filters (sales_standard/subsidized/caterer)
      // but share a single "Ventes" column, visible if any quote type is selected.
      if (type === "sales") {
        return this.filters.document_types.some((t) => t.startsWith("sales_"));
      }
      return this.filters.document_types.includes(type);
    },

    getRowTotalQuantity(row) {
      let total = 0;
      if (this.isColumnVisible('sales')) {
        total += row.total_sales_quantity || 0;
      }
      if (this.isColumnVisible('purchases')) {
        total += row.total_purchases_quantity || 0;
      }
      if (this.isColumnVisible('transfers_incoming')) {
        total += row.total_transfers_incoming_quantity || 0;
      }
      if (this.isColumnVisible('transfers_outgoing')) {
        total += row.total_transfers_outgoing_quantity || 0;
      }
      if (this.isColumnVisible('internal_invoices_incoming')) {
        total += row.total_internal_invoices_incoming_quantity || 0;
      }
      if (this.isColumnVisible('internal_invoices_outgoing')) {
        total += row.total_internal_invoices_outgoing_quantity || 0;
      }
      return total;
    },

    getRowTotalAmount(row) {
      let total = 0;
      if (this.isColumnVisible('sales')) {
        total += row.total_sales_amount || 0;
      }
      if (this.isColumnVisible('purchases')) {
        total += row.total_purchases_amount || 0;
      }
      if (this.isColumnVisible('transfers_incoming')) {
        total += row.total_transfers_incoming_amount || 0;
      }
      if (this.isColumnVisible('transfers_outgoing')) {
        total += row.total_transfers_outgoing_amount || 0;
      }
      if (this.isColumnVisible('internal_invoices_incoming')) {
        total += row.total_internal_invoices_incoming_amount || 0;
      }
      if (this.isColumnVisible('internal_invoices_outgoing')) {
        total += row.total_internal_invoices_outgoing_amount || 0;
      }
      return total;
    },

    getSummaries(param) {
      const { columns } = param;
      const sums = [];

      // Map column properties to global stats from API (not page-level sums)
      const statsMap = {
        total_sales_quantity: { value: this.stats.totalSalesQuantity, format: 'number' },
        total_sales_amount: { value: this.stats.totalSalesAmount, format: 'currency' },
        total_purchases_quantity: { value: this.stats.totalPurchasesQuantity, format: 'number' },
        total_purchases_amount: { value: this.stats.totalPurchasesAmount, format: 'currency' },
        total_transfers_incoming_quantity: { value: this.stats.totalTransfersIncomingQuantity, format: 'number' },
        total_transfers_incoming_amount: { value: this.stats.totalTransfersIncomingAmount, format: 'currency' },
        total_transfers_outgoing_quantity: { value: this.stats.totalTransfersOutgoingQuantity, format: 'number' },
        total_transfers_outgoing_amount: { value: this.stats.totalTransfersOutgoingAmount, format: 'currency' },
        total_internal_invoices_incoming_quantity: { value: this.stats.totalInternalInvoicesIncomingQuantity, format: 'number' },
        total_internal_invoices_incoming_amount: { value: this.stats.totalInternalInvoicesIncomingAmount, format: 'currency' },
        total_internal_invoices_outgoing_quantity: { value: this.stats.totalInternalInvoicesOutgoingQuantity, format: 'number' },
        total_internal_invoices_outgoing_amount: { value: this.stats.totalInternalInvoicesOutgoingAmount, format: 'currency' },
      };

      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = this.$t('COMMON.TOTALS');
          return;
        }

        const prop = column.property;

        if (prop === 'row_total_quantity') {
          sums[index] = this.formatNumber(this.getGrandTotalQuantity());
        } else if (prop === 'row_total_amount') {
          sums[index] = this.formatCurrency(this.getGrandTotalAmount());
        } else if (statsMap[prop]) {
          const { value, format } = statsMap[prop];
          sums[index] = format === 'currency'
            ? this.formatCurrency(value)
            : this.formatNumber(value);
        } else {
          sums[index] = '';
        }
      });

      return sums;
    },

    getGrandTotalQuantity() {
      let total = 0;
      if (this.isColumnVisible('sales')) total += this.stats.totalSalesQuantity || 0;
      if (this.isColumnVisible('purchases')) total += this.stats.totalPurchasesQuantity || 0;
      if (this.isColumnVisible('transfers_incoming')) total += this.stats.totalTransfersIncomingQuantity || 0;
      if (this.isColumnVisible('transfers_outgoing')) total += this.stats.totalTransfersOutgoingQuantity || 0;
      if (this.isColumnVisible('internal_invoices_incoming')) total += this.stats.totalInternalInvoicesIncomingQuantity || 0;
      if (this.isColumnVisible('internal_invoices_outgoing')) total += this.stats.totalInternalInvoicesOutgoingQuantity || 0;
      return total;
    },

    getGrandTotalAmount() {
      let total = 0;
      if (this.isColumnVisible('sales')) total += this.stats.totalSalesAmount || 0;
      if (this.isColumnVisible('purchases')) total += this.stats.totalPurchasesAmount || 0;
      if (this.isColumnVisible('transfers_incoming')) total += this.stats.totalTransfersIncomingAmount || 0;
      if (this.isColumnVisible('transfers_outgoing')) total += this.stats.totalTransfersOutgoingAmount || 0;
      if (this.isColumnVisible('internal_invoices_incoming')) total += this.stats.totalInternalInvoicesIncomingAmount || 0;
      if (this.isColumnVisible('internal_invoices_outgoing')) total += this.stats.totalInternalInvoicesOutgoingAmount || 0;
      return total;
    },

    async exportToExcel() {
      this.exportLoading = true;
      try {
        // Build same params as fetchData but without pagination
        let startDate = undefined;
        let endDate = undefined;

        if (this.dateRange) {
          if (Array.isArray(this.dateRange)) {
            startDate = this.dateRange[0];
            endDate = this.dateRange[1] || this.dateRange[0];
          } else {
            let dates = this.dateRange.split(" to ");
            if (dates.length === 1) {
              dates = this.dateRange.split(" au ");
            }
            if (dates.length === 2) {
              startDate = dates[0].trim();
              endDate = dates[1].trim();
            } else if (dates.length === 1 && dates[0]) {
              startDate = dates[0].trim();
              endDate = dates[0].trim();
            }
          }
        }

        const documentTypes = this.filters.document_types.length > 0
          ? this.filters.document_types
          : this.allDocumentTypes;

        const productIds = this.filters.products.length > 0
          ? this.filters.products.map(p => p.id)
          : undefined;

        const establishmentIds = this.filters.establishments.length > 0
          ? this.filters.establishments.map(e => e.id)
          : undefined;

        const params = {
          organization_id: this.filters.organization_id || undefined,
          establishment_ids: establishmentIds,
          start_date: startDate,
          end_date: endDate,
          document_types: documentTypes,
          product_ids: productIds,
          supplier_id: this.filters.supplier_id || undefined,
          by_warehouse: this.appliedByWarehouse ? 1 : undefined,
        };

        const service = require("@/store/services/products-report-service").default;
        const { data } = await service.getAllReport(params);

        // Build Excel rows
        const headers = [];
        if (this.appliedByWarehouse) {
          headers.push(this.$t("COMMON.WAREHOUSE"));
        }
        headers.push(
          this.$t("COMMON.CODE"),
          this.$t("COMMON.NAME"),
          this.$t("COMMON.SKU"),
        );

        const dataKeys = [];

        if (this.isColumnVisible("sales")) {
          headers.push(this.$t("COMMON.SALES") + " - " + this.$t("COMMON.QUANTITY"));
          headers.push(this.$t("COMMON.SALES") + " - " + this.$t("COMMON.AMOUNT"));
          dataKeys.push({ qty: "total_sales_quantity", amt: "total_sales_amount" });
        }
        if (this.isColumnVisible("purchases")) {
          headers.push(this.$t("COMMON.PURCHASES") + " - " + this.$t("COMMON.QUANTITY"));
          headers.push(this.$t("COMMON.PURCHASES") + " - " + this.$t("COMMON.AMOUNT"));
          dataKeys.push({ qty: "total_purchases_quantity", amt: "total_purchases_amount" });
        }
        if (this.isColumnVisible("transfers_incoming")) {
          headers.push(this.$t("COMMON.TRANSFERS_INCOMING") + " - " + this.$t("COMMON.QUANTITY"));
          headers.push(this.$t("COMMON.TRANSFERS_INCOMING") + " - " + this.$t("COMMON.AMOUNT"));
          dataKeys.push({ qty: "total_transfers_incoming_quantity", amt: "total_transfers_incoming_amount" });
        }
        if (this.isColumnVisible("transfers_outgoing")) {
          headers.push(this.$t("COMMON.TRANSFERS_OUTGOING") + " - " + this.$t("COMMON.QUANTITY"));
          headers.push(this.$t("COMMON.TRANSFERS_OUTGOING") + " - " + this.$t("COMMON.AMOUNT"));
          dataKeys.push({ qty: "total_transfers_outgoing_quantity", amt: "total_transfers_outgoing_amount" });
        }
        if (this.isColumnVisible("internal_invoices_incoming")) {
          headers.push(this.$t("COMMON.INTERNAL_INVOICES_INCOMING") + " - " + this.$t("COMMON.QUANTITY"));
          headers.push(this.$t("COMMON.INTERNAL_INVOICES_INCOMING") + " - " + this.$t("COMMON.AMOUNT"));
          dataKeys.push({ qty: "total_internal_invoices_incoming_quantity", amt: "total_internal_invoices_incoming_amount" });
        }
        if (this.isColumnVisible("internal_invoices_outgoing")) {
          headers.push(this.$t("COMMON.INTERNAL_INVOICES_OUTGOING") + " - " + this.$t("COMMON.QUANTITY"));
          headers.push(this.$t("COMMON.INTERNAL_INVOICES_OUTGOING") + " - " + this.$t("COMMON.AMOUNT"));
          dataKeys.push({ qty: "total_internal_invoices_outgoing_quantity", amt: "total_internal_invoices_outgoing_amount" });
        }

        const rows = data.map((row) => {
          const r = [];
          if (this.appliedByWarehouse) {
            r.push(row.warehouse_name || '-');
          }
          r.push(row.product_code, row.product_name, row.product_sku);
          dataKeys.forEach(({ qty, amt }) => {
            r.push(row[qty] || 0);
            r.push(row[amt] || 0);
          });
          return r;
        });

        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, this.$t("COMMON.PRODUCTS_REPORT"));

        const dateStr = startDate && endDate ? `${startDate}_${endDate}` : new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `products_report_${dateStr}.xlsx`);
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.exportLoading = false;
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("fr-CA", {
        style: "currency",
        currency: "CAD",
      }).format(amount || 0);
    },

    formatNumber(value) {
      return new Intl.NumberFormat("fr-CA").format(value || 0);
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>
